import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { emptySankeyFormValues, sankeyToForm } from 'utils/form';

import { useCurrentSankeyQuery } from '../../api';
import { SankeyFormAdditionalContext } from '../../contexts/sankeyFormContext';
import { canUserEditDiagram } from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import { sankeyObjectSchema } from '../../utils/validation';

type Props = {
  children: React.ReactNode;
};

const SankeyFormProvider: React.FC<Props> = ({ children }) => {
  const form = useNewSankeyForm();
  const { user } = useCurrentUser();

  // Keep the form in sync with the Sankey received by the route.
  // Sankey refetch only happens when the route changes or when a user
  // gets back to the browser tab after doing something else (the latter
  // is useful when editing the same diagram from multiple tabs / apps).
  // NOTE: All the editable controls are frozen while the data is being
  // fetched so that fetching wouldn't interfere with autosave in any way.
  const { data: sankey, ...query } = useCurrentSankeyQuery((sankey) => {
    form.reset(sankeyToForm(sankey));
  });

  const canUpdate = canUserEditDiagram(sankey, user);

  return (
    <FormProvider {...form}>
      <SankeyFormAdditionalContext.Provider
        value={{
          canUpdate,
          isErrorFetching: query.isError,
          isFetching: query.isFetching,
          isLoading: query.isLoading,
          // For authenticated users we show the diagram as read-only if and only
          // if they can't update it, but we let anonymous users edit example, template
          // and official diagrams (although they can't update them directly).
          isReadOnly: user?.is_authenticated
            ? !canUpdate
            : !(
                sankey?.is_example ||
                sankey?.is_template ||
                sankey?.is_official
              ),
        }}
      >
        {children}
      </SankeyFormAdditionalContext.Provider>
    </FormProvider>
  );
};

export const useNewSankeyForm = (defaultValues = emptySankeyFormValues) =>
  useForm<SankeyFormValues>({
    mode: 'onChange',
    resolver: yupResolver(sankeyObjectSchema),
    defaultValues,
  });

export default SankeyFormProvider;
